import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  AccessoryBundle, ListAccessoryBundlesRequest, ListAccessoryBundlesResponse, SituViewWebApiClient
} from 'situview-webapi-client';
import { AuthService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'accessory-bundle-config',
  templateUrl: './accessory-bundle-config.component.html',
  styleUrls: ['./accessory-bundle-config.component.less']
})
export class AccessoryBundleConfigComponent extends SituBaseComponent implements OnChanges {
  @Input() incomingAccessoryBundleId: string | undefined;
  @Input() incomingBuildingInstructions: string | undefined;
  @Input() incomingAccessoryCodes: string[] | undefined;
  @Input() homeName: string | undefined;

  @Output() onAccessoryBundleChange = new EventEmitter<AccessoryBundleChangeEvent>();

  bundleControl = new FormControl('');
  bundles: AccessoryBundle[] = [];
  filteredBundles: Observable<AccessoryBundle[]> | undefined;
  selectedBundle: AccessoryBundle | undefined;
  // Set while programmatically resolving a selection, so emits can be told apart from user changes
  private resolvingSelection: boolean = false;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private situViewClient: SituViewWebApiClient) {
      super(authService, stateService);
  }

  protected onSelectedCompanyChange(): void {
    this.loadBundles().then(() => {
      this.applyIncomingSelection();
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadBundles().then(() => {
      this.applyIncomingSelection();
    });

    this.filteredBundles = this.bundleControl.valueChanges
      .pipe(
        startWith(''),
        map(value => value ? (typeof value === 'string' ? value : value.name) : ''),
        map(name => this._filterBundles(name))
      );

    this._subscriptions.push(this.bundleControl.valueChanges.subscribe((bundle: AccessoryBundle) => {
      if (bundle && typeof bundle !== 'string') {
        this.selectBundle(bundle);
      }
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.incomingAccessoryBundleId || changes.incomingBuildingInstructions || changes.incomingAccessoryCodes) {
      this.applyIncomingSelection();
    }
    if (changes.homeName && !changes.homeName.isFirstChange()) {
      // Re-run the autocomplete filter for the new home
      this.bundleControl.setValue(this.bundleControl.value);
    }
  }

  displayBundle(bundle: AccessoryBundle): string {
    return bundle && bundle.name ? bundle.name : '';
  }

  clearBundle(): void {
    this.bundleControl.setValue('');
    this.bundleControl.reset();
    if (this.selectedBundle) {
      this.selectedBundle = undefined;
      this.onAccessoryBundleChange.emit({ accessoryBundleId: undefined, isUserChange: !this.resolvingSelection });
    }
  }

  get bundlesForHome(): AccessoryBundle[] {
    if (!this.homeName) return this.bundles;
    const home = this.homeName.toLowerCase();
    return this.bundles.filter(bundle => {
      const hasHomeInfo = bundle.items?.some(item => item.homeName);
      return !hasHomeInfo || bundle.items!.some(item => item.homeName?.toLowerCase() === home);
    });
  }

  private _filterBundles(value: string): AccessoryBundle[] {
    const candidates = this.bundlesForHome;
    if (!value) return candidates.slice();
    const filterValue = value.toLowerCase();
    return candidates.filter(bundle => bundle.name?.toLowerCase().includes(filterValue));
  }

  private selectBundle(bundle: AccessoryBundle) {
    if (this.selectedBundle?.id !== bundle.id) {
      this.selectedBundle = bundle;
      this.onAccessoryBundleChange.emit({ accessoryBundleId: bundle.id, isUserChange: !this.resolvingSelection });
    }
  }

  private loadBundles(): Promise<void> {
    if (!this.selectedCompany?.id) {
      return Promise.resolve();
    }

    let request = new ListAccessoryBundlesRequest();
    request.companyId = this.selectedCompany.id;
    request.page = 1;
    request.pageSize = 1000;

    return this.situViewClient.listAccessoryBundles(request)
      .then((response: ListAccessoryBundlesResponse) => {
        if (response.success) {
          this.bundles = response.accessoryBundles ?? [];
        } else {
          this.showMessage("Failed to load accessory bundles.");
        }
      })
      .catch((err: any) => {
        this.showMessage("Failed to load accessory bundles.");
      });
  }

  // Resolve the incoming render's bundle: by id, then buildingInstructions, then item codes.
  private applyIncomingSelection() {
    this.resolvingSelection = true;
    try {
      if (!this.incomingAccessoryBundleId && !this.incomingBuildingInstructions && !this.incomingAccessoryCodes?.length) {
        this.clearBundle();
        return;
      }
      if (this.bundles.length < 1) return;

      let bundle: AccessoryBundle | undefined;
      if (this.incomingAccessoryBundleId) {
        bundle = this.bundles.find(x => x.id === this.incomingAccessoryBundleId);
      }
      if (!bundle && this.incomingBuildingInstructions) {
        bundle = this.bundles.find(x => x.buildingInstructions?.toLowerCase() === this.incomingBuildingInstructions!.toLowerCase());
      }
      if (!bundle && this.incomingAccessoryCodes?.length) {
        const incoming = this.normaliseCodes(this.incomingAccessoryCodes);
        bundle = this.bundles.find(x => this.normaliseCodes(x.items?.map(item => item.code!) ?? []) === incoming);
      }

      if (bundle) {
        this.bundleControl.setValue(bundle);
      } else {
        // Render has accessories but no bundle matches (e.g. bundle since deleted)
        this.clearBundle();
      }
    } finally {
      this.resolvingSelection = false;
    }
  }

  // Company-prefixed codes ("company.code") compare equal to their bare form.
  private normaliseCodes(codes: string[]): string {
    return codes
      .map(code => code?.toLowerCase().split('.').pop() ?? '')
      .filter(code => code)
      .sort()
      .join(',');
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

export interface AccessoryBundleChangeEvent {
  accessoryBundleId: string | undefined;
  isUserChange: boolean;
}
